import '../tooltip/tooltip';
import '../chat-loader/chat-loader';
import './browser-header';
import { attachEvent, requestHandler, showMessage } from '../../js/tools';
import fEvents from '../../js/events';
import { BrowserHeader } from './browser-header';

enum BrowserActions {
    CLICK = 'click',
    TYPE = 'type',
    COMMAND = 'command',
    close = 'close'
}

const COMMAND_KEYS = new Set([
    'Backspace',
    'Enter',
    'Escape',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Tab'
]);

export default class BrowserStream extends HTMLElement {
    clickedPosition: { x: number; y: number; imageWidth: number; imageHeight: number; };
    private eventsInitialized = false;
    // The url to the browser actions like clicks and typing
    private browserId: string;
    // The time it takes to wait for the browser to load
    private browserTimeout: number;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.clickedPosition = { x: 0, y: 0, imageWidth: 0, imageHeight: 0 };

        this.browserTimeout = parseInt(this.getAttribute('data-timeout'), 10) || 10000; // 10 seconds
        this.render();
    }

    // observe attribute changes
    static get observedAttributes() {
        return ['csrftoken'];
    }

    connectedCallback() {
        const currentWindowSize = window.innerWidth;
        if (currentWindowSize < 480) {
            this.elements.keyboardToolBtn.style.display = 'flex';
        }
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'csrftoken' && newValue) {
            this.startBrowser();

            if (!this.eventsInitialized) {
                this.setupUiEvents();
                this.setupConnectionEvents();
                this.eventsInitialized = true;
            }
        }
    }

    public endSession = () => {
        this.elements.browserHeader.endSession();
    };

    private startBrowser = () => {
        this.openBrowser();
        const csrfToken = this.getAttribute('csrftoken');
        this.elements.browserHeader.connect(csrfToken, this.browserTimeout);
        window.addEventListener('keyup', this.handleKeyPress);
    };

    private setupConnectionEvents = () => {
        attachEvent('browser-header', 'data', (e: CustomEvent) => {
            const data = e.detail as any;

            const streamImageViewport = this.elements.browserStreamImage;
            streamImageViewport.src = `data:image/png;base64,${data.image}`;
            this.browserId = data.browserId;
        }, this.shadowRoot);

        attachEvent('browser-header', 'stateChange', (e: CustomEvent) => {
            const data = e.detail;
            const reconnectingStates = ['SESSION_CLOSED', 'RECONNECTING', 'SESSION_ENDED', 'DISCONNECTED'];
            if (data.state === 'SESSION_CLOSED') {
                this.close();
            } else if (data.state === 'CONNECTED') {
                this.browserScreenLoader().stop();
            } else if (reconnectingStates.includes(data.state)) {
                this.browserScreenLoader().start();
            }
        }, this.shadowRoot);
    };

    private closePastedTextInput = () => {
        const pasteInput = this.shadowRoot.querySelector('.browser-paste-input') as HTMLElement;
        pasteInput.style.display = 'none';
        const textInput = this.shadowRoot.querySelector('.browser-paste-input--text') as HTMLInputElement;
        textInput.value = '';
        textInput.blur();
        const pasteButton = this.shadowRoot.querySelector('.paste-btn') as HTMLElement;
        pasteButton.classList.remove('flex-open');
        window.addEventListener('keyup', this.handleKeyPress);
    };

    private openPastedTextInput = () => {
        const pasteInput = this.shadowRoot.querySelector('.browser-paste-input') as HTMLElement;
        pasteInput.style.display = 'flex';
        const textInput = this.shadowRoot.querySelector('.browser-paste-input--text') as HTMLInputElement;
        textInput.focus();
        const pasteButton = this.shadowRoot.querySelector('.paste-btn') as HTMLElement;
        pasteButton.classList.add('flex-open');
        window.removeEventListener('keyup', this.handleKeyPress);
    };

    private setupUiEvents() {
        attachEvent('.image-stream-viewport', 'click', e => {
            const pasteButton = this.shadowRoot.querySelector('.paste-btn') as HTMLElement;
            if (pasteButton.classList.contains('flex-open')) {
                // If the paste input is open, close it and stop listening for key presses
                this.closePastedTextInput();
            }

            const imageSrcEvent = e.target.getAttribute('src');
            if (!imageSrcEvent || imageSrcEvent === '') return;

            e.preventDefault();
            const event = e as MouseEvent;
            const container = e.target as HTMLDivElement;
            const imageWidth = container.clientWidth;
            const imageHeight = container.clientHeight;
            const rect = container.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            this.clickedPosition = { x, y, imageWidth, imageHeight };
            const input = this.shadowRoot.querySelector('.trigger-open-keyboard-input') as HTMLInputElement;
            if (input) {
                input.style.position = 'absolute';
                input.style.left = `${x}px`;
                input.style.top = `${y}px`;
            }
            void this.sendBrowserAction(BrowserActions.CLICK, { x, y, imageWidth, imageHeight });
        }, this.shadowRoot);
        attachEvent('.keyboard-tool-btn', 'click', () => {
            // open the keyboard with an inout field to type, just call focus
            (this.shadowRoot.querySelector('.trigger-open-keyboard-input') as HTMLInputElement).focus();
        }, this.shadowRoot);

        // To open the Input field for pasting text
        attachEvent('.paste-btn', 'click', (e) => {

            const isOpened = e.target.classList.contains('flex-open');
            if (isOpened) {
                this.closePastedTextInput();
                return;
            }

            // Show the paste input field
            this.openPastedTextInput();
        }, this.shadowRoot);

        attachEvent('.paste-button', 'click', async (e) => {
            e.preventDefault();
            const textInput = this.shadowRoot.querySelector('.browser-paste-input--text') as HTMLInputElement;
            const text = textInput.value.trim();
            if (text) {
                await this.sendBrowserAction(BrowserActions.TYPE, this.clickedPosition, text);
                this.closePastedTextInput();
            } else {
                showMessage('Please enter some text to paste.');
            }
        }, this.shadowRoot);
    }

    get elements () {
        return {
            browser: this.shadowRoot.querySelector('.browser') as HTMLElement,
            browserHeader: this.shadowRoot.querySelector('browser-header') as BrowserHeader,
            browserContent: this.shadowRoot.querySelector('.browser-content') as HTMLElement,
            browserStreamImage: this.shadowRoot.querySelector('.image-stream-viewport') as HTMLImageElement,
            browserLoaderScreen: this.shadowRoot.querySelector('.browser-loader') as HTMLElement,
            browserHeaderText: this.shadowRoot.querySelector('.browser-header-text') as HTMLElement,
            keyboardToolBtn: this.shadowRoot.querySelector('.keyboard-tool-btn') as HTMLElement
        };
    }

    get csrfToken() {
        return this.getAttribute('csrftoken');
    }

    private handleKeyPress = (e: KeyboardEvent) => {
        // Prevent actions on modifiers if you want a clean input.
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(e.key)) {
            return;
        }

        // Decide if this key is a command or just text.
        if (COMMAND_KEYS.has(e.key)) {
        // Send the command to the server.
            void this.sendBrowserAction(BrowserActions.COMMAND, this.clickedPosition, e.key);
            e.preventDefault(); // Prevent default behavior
        } else {
        // Send normal text.
            void this.sendBrowserAction(BrowserActions.TYPE, this.clickedPosition, e.key);
        }

    };

    private browserScreenLoader = () => {
        return {
            start: () => {
                const loader = this.shadowRoot.querySelector('.browser-loader') as HTMLElement;
                loader.classList.add('flex-open');
            },
            message: (message: string) => {
                this.elements.browserHeaderText.innerHTML = message;
            },
            clickScreen: (x, y) => {
                // add a ripple effect where the user clicked
                const ripple = document.createElement('div');
                ripple.classList.add('ripple');
                ripple.style.width = ripple.style.height = '100px'; // Ripple size
                ripple.style.left = `${x - 50}px`; // Center the ripple on the cursor
                ripple.style.top = `${y - 50}px`; // Center the ripple on the cursor

                this.elements.browserContent.appendChild(ripple);
                setTimeout(() => {
                    this.elements.browserContent.removeChild(ripple);
                }, 600);
            },
            stop: () => {
                const loader = this.shadowRoot.querySelector('.browser-loader') as HTMLElement;
                loader.classList.remove('flex-open');
                this.elements.browserHeaderText.innerHTML = '';
            }
        };
    };

    async sendBrowserAction (action: BrowserActions, position?: { x: number, y: number, imageWidth: number, imageHeight: number}, text?: string) {
        if (!this.browserId) {
            console.error('Browser ID is not set');
            return;
        }

        this.browserScreenLoader().start();
        switch (action) {
            case BrowserActions.CLICK:
                {
                    const data = await this.sendActionToServer(action, position);
                    if (data.success) {
                        this.browserScreenLoader().clickScreen(position.x, position.y);
                        this.browserScreenLoader().stop();
                    } else {
                        this.browserScreenLoader().stop();
                        this.elements.browserHeader.reconnectBrowser();
                    }
                }
                break;
            case BrowserActions.TYPE:
            case BrowserActions.COMMAND: {
                this.browserScreenLoader().start();
                this.browserScreenLoader().message(action === BrowserActions.TYPE ? 'Typing, please wait...' : 'Sending Command, please wait...');
                const data = await this.sendActionToServer(action, position, text);
                if (data) {
                    this.browserScreenLoader().stop();
                } else {
                    this.browserScreenLoader().stop();
                    this.elements.browserHeader.reconnectBrowser();
                }
            }
                break;
            case BrowserActions.close:
                await this.sendActionToServer(action);
                break;
            default:
                break;
        }
    }

    private waitFor = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    private sendActionToServer = (action: BrowserActions, position?: { x: number, y: number, imageWidth: number, imageHeight: number}, key?: string) : Promise<{ success: boolean }> => {
        return new Promise(resolve=> {
            requestHandler({
                url: '/v2/linkedin/send-browser-action',
                payload: JSON.stringify({ csrfToken: this.csrfToken, action, position, key, browserId: this.browserId }),
                method: 'POST',
                isJson: true
            }).then(r => {
                // if success, unload the page and add a ripple effect where the user clicked
                const parsedData = JSON.parse(r.data);
                if (parsedData.success) {
                    resolve({ success: true });
                } else {
                    resolve({ success: false });
                }
            })
                .catch(error => {
                    showMessage(error?.message || 'An error occurred, please try again');
                });
        });
    };

    private openBrowser = () => {
        this.shadowRoot.querySelector('.browser').classList.add('flex-open');
    };

    private close = () => {
        this.shadowRoot.querySelector('.browser').classList.remove('flex-open');
        this.elements.browserStreamImage.src = '';
        window.removeEventListener('keyup', this.handleKeyPress);
        this.browserScreenLoader().stop();
        void this.sendBrowserAction(BrowserActions.close);
        fEvents.dispatch(this, 'session-ended', {});
    };

    render = () => {
        this.shadowRoot.innerHTML = `
        <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
        <link rel="stylesheet" href="/styles/index.css">
        <style>
            :host { width: 100%; display: flex; justify-content: center; }
            .browser { display: none; flex-direction: column; border: 2px solid #ffffff33; background: #eeeeee; z-index: 99; width: 350px; border-top-right-radius: 10px; border-top-left-radius: 10px; }
            .browser-header { flex: 1; background: #eeeeee; border-top-left-radius: 10px; border-top-right-radius: 10px; display: flex; align-items: center; justify-content: space-between; padding: 0 16px; position: sticky; top: 0; z-index: 99999; }
            .browser-content { background: #fff; position: relative; }
            .browser-action { display: flex; align-items: center; border-radius: 4px; border: none; padding: 4px 8px; cursor: pointer; background: #b12e2e; color: white; gap: 4px; }
            .loader-wrapper--backdrop { height: 100%; display: flex; justify-content: center; align-items: center; position: absolute; top: 0; right: 0; bottom: 0; left: 0; }
            .browser-loader { position: relative; height: 100%; width: 100%; display: none; position: absolute; top: 0; }
            .browser-loader { background: #e4e4e496; }
            .close { display: none; }
            .flex-open { display: flex; }
            .loader-wrapper { width: 100%; padding: 32px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .loader-message { display: flex; flex-direction: column; align-items: center; }
            .ripple { position: absolute; border-radius: 50%; background: rgb(0 0 0 / 93%); /* light white ripple */ transform: scale(0); animation: ripple-animation 600ms linear; pointer-events: none; /* Prevents the effect from blocking click events */ }

            @keyframes ripple-animation {
                to {
                    transform: scale(1);
                    opacity: 0;
                }
            }

            .image-stream-viewport { width: 350px; height: 600px; cursor: pointer; }
            .key-label { padding: 4px 12px 6px 12px; background: #3f3c3c; color: white; font-size: 20px; border-radius: 4px; }
            .key-label-command { text-transform: uppercase; }
            .browser-header-text { display: flex; align-items: center; gap: 8px; position: absolute; bottom: -5px; z-index: 1; left: 16px; font-size: 12px; font-weight: 500; }
            .keyboard-tool-btn { display: none; width:100%; position: sticky; bottom: 0; left:0; background: #3f3c3c; color: white; padding: 8px 16px; border: none; align-items: center; gap: 8px; cursor: pointer; }

            @keyframes swipe-hand { 25% { transform: translate(20px) rotate(30deg); } 50% { transform: translate(-20px) rotate(-15deg); } 100% { transform: translate(0px) rotate(0); } }
            @keyframes swipe-dot { 12% { visibility: visible; width: 40px; } 25% { visibility: visible; transform: translate(-65px); width: 20px; } 26% { visibility: hidden; } }

            .keyboard-tool-icon { font-size: 32px; }
            .browser-paste-input { display: none; position: absolute; width: 325px; top: 50px; left: 0; align-items: center; gap: 8px; height: 32px; padding: 13px; background: #eeeeee; }
            .browser-paste-input--text { flex: 1; border: 1px solid #000000; font-size: 14px; }
            .paste-btn { margin-left: 32px; background: var(--primary); color: white; }
            .browser-action-btn { display: flex; align-items: center; border-radius: 4px; border: none; padding: 4px 8px; cursor: pointer; gap: 4px; font-size: 12px; }
        </style>

        <div class="browser">
             <div class="browser-header">
                <h3>Browser</h3>
                <div class="browser-header-text"></div>
                <button class="paste-btn browser-action-btn" icon="keyboard" title="Paste text">
                    <i class="material-icons">keyboard</i>
                    Paste text
                </button>

                <fl-tooltip for=".paste-btn">
                   Paste the copied text into the browser.
                </fl-tooltip>

                <browser-header></browser-header>

                <div class="browser-paste-input">
                    <input type="text" class="browser-paste-input--text" placeholder="Paste copied text here or type text here" />
                        <icon-button class="paste-button" icon="send" title="Send text to browser">Send</icon-button>
                </div>
            </div>

            <div class="browser-content" id="fit-screen">
                <img class="image-stream-viewport" id="fit-screen"/>

                <button class="keyboard-tool-btn">
                    <i class="material-icons keyboard-tool-icon ">keyboard</i> <span class="key-label">Open Keyboard</span>
                </button>

                <div class="browser-loader">
                    <div class="loader-wrapper">
                        <chat-loader></chat-loader>
                        <div class="loader-message"></div>
                    </div>
                </div>
                <input class="trigger-open-keyboard-input" type="text" style="position: absolute; left: -9999px; opacity: 0; width: 0px; height: 0px; pointer-events: none;"  />
            </div>
        </div>
        `;
    };

}

customElements.define('browser-stream', BrowserStream);
